import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth, API, usePublicConfig } from "../App";
import { useThemeTexts } from "../components/ThemeProvider";
import { useJsonLd, buildHomeJsonLd } from "../lib/seo";
import axios from "axios";
import { toast } from "sonner";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Select, SelectContent, SelectItem, SelectSeparator, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import PrivacyCheckbox from "../components/PrivacyCheckbox";
import TermsCheckbox from "../components/TermsCheckbox";
import FlightDetailsFields, { buildFlightDetailsPayload } from "../components/FlightDetailsFields";
import VeniceSubPicker, { VENICE_LABEL, VENICE_NEAR, VENICE_WATER_TAXI, VENICE_HOTEL, VENICE_AIRPORT, isVeniceVariant, venicedbKey, veniceDisplayLabel } from "../components/VeniceSubPicker";
import SearchableSelect from "../components/SearchableSelect";
import SpecificAddressField from "../components/SpecificAddressField";
import GuestRegistrationPrompt from "../components/GuestRegistrationPrompt";
import SiteNav from "../components/SiteNav";
import PopularDestinations from "../components/PopularDestinations";
import { isDateSelectable, filterSlotsForDate, earliestPickupLabel } from "../lib/bookingCutoff";
import { isValidInternationalPhone, INTERNATIONAL_PHONE_HINT } from "../lib/phoneValidation";
import { extractApiError } from "../lib/apiError";
import { renderMarkdownSafe } from "../lib/safeMarkdown";
import { format } from "date-fns";
import { 
  CalendarIcon, 
  MapPin, 
  Clock, 
  Users, 
  Briefcase, 
  Car, 
  Phone, 
  Mail, 
  Moon, 
  ChevronRight,
  Star,
  Shield,
  CheckCircle,
  User,
  Menu,
  X,
  LogOut,
  ArrowRight,
  ArrowDownUp,
  Repeat,
  Percent
} from "lucide-react";

const HERO_BG = "https://customer-assets.emergentagent.com/job_italy-ride-quotes/artifacts/35ncp1a8_Image.jpeg";
const CHAUFFEUR_IMG = "https://customer-assets.emergentagent.com/job_italy-ride-quotes/artifacts/9hi7jcs1_IMG_7955.JPG";
const INTERIOR_IMG = "https://customer-assets.emergentagent.com/job_italy-ride-quotes/artifacts/3rthndvn_IMG_7957.JPG";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const resolveAsset = (url, fallback) => {
  const u = (url || "").trim();
  if (!u) return fallback;
  if (u.startsWith("/api/")) return `${BACKEND_URL}${u}`;
  return u;
};

const TIME_INTERVAL_MINUTES = 30;
const TIME_SLOTS = [];
for (let h = 0; h < 24; h++) {
  for (let m = 0; m < 60; m += TIME_INTERVAL_MINUTES) {
    const hour = h.toString().padStart(2, '0');
    const minute = m.toString().padStart(2, '0');
    TIME_SLOTS.push(`${hour}:${minute}`);
  }
}

const PRIORITY_LOCATIONS = [VENICE_LABEL];

export default function HomePage() {
  const { user, login, logout } = useAuth();
  const t = useThemeTexts();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const publicConfig = usePublicConfig();
  const homeJsonLd = useMemo(
    () => buildHomeJsonLd({ whatsappNumber: publicConfig.whatsapp_number }),
    [publicConfig.whatsapp_number]
  );
  useJsonLd(homeJsonLd);
  const [allRoutes, setAllRoutes] = useState([]);
  const [bookingSettings, setBookingSettings] = useState(null);
  const [veniceHotels, setVeniceHotels] = useState([]);   // Loaded once on mount
  const [luxuryOptions, setLuxuryOptions] = useState([]);
  const [fleet, setFleet] = useState({ vehicles: [], max_passengers: 12, max_luggage: 12 });
  const [luxuryDetail, setLuxuryDetail] = useState(null); // option being viewed in the "details" popup
  const [pricePreview, setPricePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [returnDatePickerOpen, setReturnDatePickerOpen] = useState(false);
  const [showGuestPrompt, setShowGuestPrompt] = useState(false);
  const [heroBgIndex, setHeroBgIndex] = useState(0);

  // Parse hero gallery from theme (newline-separated URLs). Resolves backend
  // relative paths via REACT_APP_BACKEND_URL. Falls back to the static HERO_BG
  // when empty so the page never renders without a background.
  const heroBgList = useMemo(() => {
    const raw = (t.hero_background_gallery || "").trim();
    if (!raw) return [HERO_BG];
    const backend = process.env.REACT_APP_BACKEND_URL || "";
    return raw
      .split(/[\r\n]+/)
      .map((u) => u.trim())
      .filter(Boolean)
      .map((u) => (u.startsWith("/api/") ? `${backend}${u}` : u));
  }, [t.hero_background_gallery]);

  useEffect(() => {
    if (heroBgList.length <= 1) return;
    const id = setInterval(() => {
      setHeroBgIndex((i) => (i + 1) % heroBgList.length);
    }, 6000);
    return () => clearInterval(id);
  }, [heroBgList.length]);
  
  const [formData, setFormData] = useState({
    origin: "",
    destination: "",
    routeId: "",
    pickupDate: null,
    pickupTime: "10:00",
    returnDate: null,
    returnTime: "10:00",
    passengers: 2,
    luggage: 1,
    isLuxury: false,
    luxuryOptionIds: [],
    isRoundTrip: false,
    userName: user?.name || "",
    userEmail: user?.email || "",
    userPhone: "",
    notes: "",
    privacyAccepted: false,
    termsAccepted: false,
    flightDetails: null,
    originSpecificAddress: "",
    destinationSpecificAddress: "",
    veniceHotelId: null            // Set when user picks a "Hotel in Venice" sub-option
  });

  const fetchRoutes = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/routes`);
      setAllRoutes(response.data);
    } catch (error) {
      console.error("Failed to fetch routes", error);
      toast.error("Could not load available routes");
    }
  }, []);

  const fetchBookingSettings = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/booking-settings`);
      setBookingSettings(response.data);
    } catch (error) {
      // Non-blocking: calendar/time will gracefully fall back to client heuristics
      console.error("Failed to fetch booking settings", error);
    }
  }, []);

  const calculatePrice = useCallback(async () => {
    try {
      const response = await axios.post(`${API}/calculate-price`, {
        origin: formData.origin,
        destination: formData.destination,
        pickup_time: formData.pickupTime,
        is_luxury: formData.isLuxury,
        luxury_option_ids: formData.luxuryOptionIds,
        passengers: formData.passengers,
        route_id: formData.routeId,
        is_round_trip: formData.isRoundTrip,
        return_time: formData.returnTime,
        venice_hotel_id: formData.veniceHotelId || null,
      });
      setPricePreview(response.data);
    } catch (error) {
      // Price preview is best-effort; reset and log silently
      console.warn("Price preview failed", error?.response?.status);
      setPricePreview(null);
    }
  }, [formData.origin, formData.destination, formData.pickupTime, formData.isLuxury, formData.luxuryOptionIds, formData.passengers, formData.routeId, formData.isRoundTrip, formData.returnTime, formData.veniceHotelId]);

  useEffect(() => {
    fetchRoutes();
    fetchBookingSettings();
    axios.get(`${API}/luxury-options`).then((r) => setLuxuryOptions(r.data || [])).catch(() => {});
    axios.get(`${API}/vehicles`).then((r) => setFleet(r.data || { vehicles: [], max_passengers: 12, max_luggage: 12 })).catch(() => {});
    axios.get(`${API}/venice-hotels`).then((r) => setVeniceHotels(r.data || [])).catch(() => {});
  }, [fetchRoutes, fetchBookingSettings]);

  // Preselect Airport → [Hotel] when landing on ?hotel=<slug> (from a hotel detail page CTA).
  useEffect(() => {
    const hotelSlug = searchParams.get("hotel");
    if (!hotelSlug || veniceHotels.length === 0) return;
    const hotel = veniceHotels.find((h) => h.slug === hotelSlug);
    if (!hotel) return;
    setFormData((prev) => ({
      ...prev,
      origin: VENICE_AIRPORT,
      destination: VENICE_HOTEL,
      veniceHotelId: hotel.hotel_id,
      routeId: "",
    }));
    // Scroll booking form into view for immediate action.
    setTimeout(() => {
      const el = document.querySelector('[data-testid="destination-select"]');
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 200);
  }, [searchParams, veniceHotels]);

  // If the currently-selected pickup time is no longer valid for the chosen date
  // (e.g. user picked today but cutoff pushes earliest to 13:00), auto-snap to the first valid slot.
  useEffect(() => {
    if (!bookingSettings || !formData.pickupDate) return;
    const valid = filterSlotsForDate(TIME_SLOTS, bookingSettings, formData.pickupDate);
    if (valid.length === 0) return;
    if (!valid.includes(formData.pickupTime)) {
      setFormData(prev => ({ ...prev, pickupTime: valid[0] }));
    }
  }, [bookingSettings, formData.pickupDate, formData.pickupTime]);

  // Email verification status banners
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    const v = sp.get("verify");
    if (v === "ok") toast.success("Email verified! You can now sign in.");
    else if (v === "expired") toast.error("Verification link expired. Please request a new one.");
    else if (v === "invalid") toast.error("Invalid verification link.");
    if (v) {
      sp.delete("verify");
      const newQ = sp.toString();
      window.history.replaceState({}, "", window.location.pathname + (newQ ? `?${newQ}` : "") + window.location.hash);
    }
  }, []);

  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        userName: user.name || "",
        userEmail: user.email || ""
      }));
    }
  }, [user]);

  useEffect(() => {
    // Fire price preview for either:
    //  - a normal route (routeId is set), or
    //  - a Venice-Hotel booking (veniceHotelId set, no route_id because
    //    Airport ↔ Hotel has no DB route).
    if ((formData.routeId || formData.veniceHotelId) && formData.pickupTime) {
      calculatePrice();
    } else {
      setPricePreview(null);
    }
  }, [formData.routeId, formData.veniceHotelId, formData.pickupTime, formData.isLuxury, formData.luxuryOptionIds, formData.passengers, formData.isRoundTrip, formData.returnTime, calculatePrice]);

  // Derive unique locations (origins + destinations) for the FROM dropdown.
  // Collapse "Venice - Piazzale Roma" / "Venice Airport" to a single "Venice" entry.
  const origins = useMemo(() => {
    const set = new Set();
    allRoutes.forEach(r => {
      set.add(veniceDisplayLabel(r.origin));
      set.add(veniceDisplayLabel(r.destination));
    });
    const all = Array.from(set);
    const priority = PRIORITY_LOCATIONS.filter(p => all.includes(p));
    const rest = all.filter(l => !PRIORITY_LOCATIONS.includes(l)).sort();
    return { priority, rest };
  }, [allRoutes]);

  // Filter destinations based on selected FROM (matches either direction; reversed = same price)
  const destinations = useMemo(() => {
    if (!formData.origin) return { priority: [], rest: [] };
    const originKey = venicedbKey(formData.origin);
    const map = new Map();
    allRoutes.forEach(r => {
      if (r.origin === originKey) {
        const dispDest = veniceDisplayLabel(r.destination);
        if (!map.has(dispDest)) map.set(dispDest, { destination: dispDest, routeId: r.route_id });
      } else if (r.destination === originKey) {
        const dispDest = veniceDisplayLabel(r.origin);
        if (!map.has(dispDest)) map.set(dispDest, { destination: dispDest, routeId: r.route_id });
      }
    });
    // When origin is a specific Venice variant (Airport / Piazzale / Near / Water Taxi / Hotel),
    // also expose "Venice" as a destination so users can book Water Taxi / Hotel-in-Venice /
    // Near-Venice trips that stay within Venice (revealed via the destination VeniceSubPicker).
    // Also required so the /?hotel=<slug> deep-link from a hotel detail page can preselect
    // the destination correctly.
    if (isVeniceVariant(formData.origin) && formData.origin !== VENICE_LABEL) {
      if (!map.has(VENICE_LABEL)) {
        map.set(VENICE_LABEL, { destination: VENICE_LABEL, routeId: "__venice_local__" });
      }
    }
    const all = Array.from(map.values());
    const priority = PRIORITY_LOCATIONS
      .map(p => all.find(d => d.destination === p))
      .filter(Boolean);
    const rest = all
      .filter(d => !PRIORITY_LOCATIONS.includes(d.destination))
      .sort((a, b) => a.destination.localeCompare(b.destination));
    return { priority, rest };
  }, [allRoutes, formData.origin]);

  // Get popular routes for showcase
  const popularRoutes = useMemo(() => {
    return allRoutes.filter(r => r.seo_priority === "Top").slice(0, 6);
  }, [allRoutes]);

  // Resolve route_id from origin/destination (handles Venice variants).
  // Returns "" while either side is the bare "Venice" placeholder (not yet sub-picked).
  const resolveRouteId = useCallback((origin, destination) => {
    if (!origin || !destination) return "";
    if (origin === VENICE_LABEL || destination === VENICE_LABEL) return "";
    const o = venicedbKey(origin);
    const d = venicedbKey(destination);
    const route = allRoutes.find(r =>
      (r.origin === o && r.destination === d) ||
      (r.origin === d && r.destination === o)
    );
    return route?.route_id || "";
  }, [allRoutes]);

  const handleOriginChange = (value) => {
    // value here is the dropdown display label (could be "Venice" placeholder)
    setFormData(prev => ({ ...prev, origin: value, destination: "", routeId: "", originSpecificAddress: "", destinationSpecificAddress: "", veniceHotelId: null }));
    setPricePreview(null);
  };

  const handleDestinationChange = (value) => {
    // Radix Select can fire onValueChange("") transiently when the SelectItem list
    // updates in the same commit as the controlled value (e.g. our /?hotel= prefill,
    // where destinations become available at the same time we set destination=Venice-Hotel).
    // Ignore these spurious clears so we don't wipe the prefill state.
    if (value === "") return;
    setFormData(prev => ({
      ...prev,
      destination: value,
      destinationSpecificAddress: "",
      veniceHotelId: prev.origin === VENICE_HOTEL ? prev.veniceHotelId : null,
      routeId: resolveRouteId(prev.origin, value)
    }));
  };

  const handleVeniceOriginPick = (fullLabel) => {
    setFormData(prev => ({
      ...prev,
      origin: fullLabel,
      // Clear address when leaving an address-requiring variant
      originSpecificAddress: (fullLabel === VENICE_NEAR || fullLabel === VENICE_WATER_TAXI) ? prev.originSpecificAddress : "",
      // Clear hotel selection when moving away from Hotel-in-Venice
      veniceHotelId: fullLabel === VENICE_HOTEL || prev.destination === VENICE_HOTEL ? prev.veniceHotelId : null,
      routeId: resolveRouteId(fullLabel, prev.destination)
    }));
  };

  const handleVeniceDestinationPick = (fullLabel) => {
    setFormData(prev => ({
      ...prev,
      destination: fullLabel,
      destinationSpecificAddress: (fullLabel === VENICE_NEAR || fullLabel === VENICE_WATER_TAXI) ? prev.destinationSpecificAddress : "",
      veniceHotelId: fullLabel === VENICE_HOTEL || prev.origin === VENICE_HOTEL ? prev.veniceHotelId : null,
      routeId: resolveRouteId(prev.origin, fullLabel)
    }));
  };

  const handleSwapRoute = () => {
    if (!formData.origin || !formData.destination) return;
    // Reverse direction with SAME route_id and SAME price - per business rule
    setFormData(prev => ({
      ...prev,
      origin: prev.destination,
      destination: prev.origin,
      originSpecificAddress: prev.destinationSpecificAddress,
      destinationSpecificAddress: prev.originSpecificAddress,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.origin || !formData.destination || !formData.pickupDate || !formData.userName || !formData.userEmail || !formData.userPhone) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (!isValidInternationalPhone(formData.userPhone)) {
      toast.error(INTERNATIONAL_PHONE_HINT);
      return;
    }

    if (formData.origin === VENICE_LABEL || formData.destination === VENICE_LABEL) {
      toast.error("Please choose Piazzale Roma, Airport, Near Venice or Water Taxi for Venice");
      return;
    }

    // Venice → Venice routes: same variant on both sides isn't a real trip
    if (isVeniceVariant(formData.origin) && isVeniceVariant(formData.destination) && formData.origin === formData.destination) {
      toast.error("Pickup and drop-off cannot be the same Venice point — please pick two different points");
      return;
    }

    // Validate specific-address fields are non-empty when "Near Venice" / "Water Taxi"
    // or the Specific-address toggle is active.
    const originAddrRaw = (formData.originSpecificAddress || "").trim();
    const destAddrRaw = (formData.destinationSpecificAddress || "").trim();
    if (formData.origin === VENICE_NEAR && !originAddrRaw) {
      toast.error("Please enter the pickup address near Venice (within 15 km)");
      return;
    }
    if (formData.destination === VENICE_NEAR && !destAddrRaw) {
      toast.error("Please enter the drop-off address near Venice (within 15 km)");
      return;
    }
    if (formData.origin === VENICE_WATER_TAXI && !originAddrRaw) {
      toast.error("Please enter the pickup hotel/address in Venice City Center (water taxi)");
      return;
    }
    if (formData.destination === VENICE_WATER_TAXI && !destAddrRaw) {
      toast.error("Please enter the drop-off hotel/address in Venice City Center (water taxi)");
      return;
    }
    if (formData.originSpecificAddress && !originAddrRaw) {
      toast.error(`Please enter the specific pickup address (within 15 km of ${formData.origin})`);
      return;
    }
    if (formData.destinationSpecificAddress && !destAddrRaw) {
      toast.error(`Please enter the specific drop-off address (within 15 km of ${formData.destination})`);
      return;
    }

    if (formData.isRoundTrip && !formData.returnDate) {
      toast.error("Please select a return date");
      return;
    }

    if (!formData.privacyAccepted) {
      toast.error("Please accept the Privacy Policy");
      return;
    }

    if (!formData.termsAccepted) {
      toast.error("Please accept the Terms of Service and Cancellation Policy");
      return;
    }

    // Guest users see the registration prompt first.
    // Logged-in users skip straight to submission.
    if (!user) {
      setShowGuestPrompt(true);
      return;
    }

    await performSubmit({ thenLogin: false });
  };

  const performSubmit = async ({ thenLogin }) => {
    const originAddrRaw = (formData.originSpecificAddress || "").trim();
    const destAddrRaw = (formData.destinationSpecificAddress || "").trim();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/quotes`, {
        user_email: formData.userEmail,
        user_name: formData.userName,
        user_phone: formData.userPhone,
        origin: formData.origin,
        destination: formData.destination,
        origin_specific_address: originAddrRaw || null,
        destination_specific_address: destAddrRaw || null,
        route_id: formData.routeId,
        pickup_date: format(formData.pickupDate, "yyyy-MM-dd"),
        pickup_time: formData.pickupTime,
        passengers: formData.passengers,
        luggage: formData.luggage || 1,
        is_luxury: formData.isLuxury,
        luxury_option_ids: formData.luxuryOptionIds || [],
        is_round_trip: formData.isRoundTrip,
        return_date: formData.isRoundTrip && formData.returnDate ? format(formData.returnDate, "yyyy-MM-dd") : null,
        return_time: formData.isRoundTrip ? formData.returnTime : null,
        notes: formData.notes,
        privacy_accepted: formData.privacyAccepted,
        terms_accepted: formData.termsAccepted,
        flight_details: buildFlightDetailsPayload(formData.flightDetails),
        venice_hotel_id: formData.veniceHotelId || null
      });

      setShowGuestPrompt(false);
      toast.success("Quote request submitted!");
      if (thenLogin) {
        // Stash the quote_id so we could optionally show a "we'll link it" toast post-login.
        try { sessionStorage.setItem("pending_quote_id", response.data.quote_id); } catch (e) { console.warn("sessionStorage set failed (private mode?)", e); }
        login();
        return;
      }
      navigate(`/quote-confirmation/${response.data.quote_id}`);
    } catch (error) {
      console.error("Quote submit failed:", error?.response?.status, error?.response?.data, error);
      toast.error(extractApiError(error, "Failed to submit quote request"));
    } finally {
      setLoading(false);
    }
  };

  const isNightService = () => {
    if (!formData.pickupTime) return false;
    const hour = parseInt(formData.pickupTime.split(":")[0]);
    return hour >= 20 || hour < 7;
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Guest registration prompt (only fires when !user and they hit submit) */}
      <GuestRegistrationPrompt
        open={showGuestPrompt}
        onClose={() => setShowGuestPrompt(false)}
        onContinueAsGuest={() => performSubmit({ thenLogin: false })}
        onSignInAndSubmit={() => performSubmit({ thenLogin: true })}
        submitting={loading}
      />
      {/* Navigation — shared component (includes All Routes dropdown submenu) */}
      <SiteNav />

      {/* Hero Section */}
      <section 
        className="hero-bg relative min-h-screen flex items-center overflow-hidden"
        data-testid="hero-section"
      >
        {/* Rotating background gallery — admin-editable via Theme → Branding */}
        {heroBgList.map((src, i) => (
          <div
            key={src + i}
            aria-hidden="true"
            className="absolute inset-0 bg-cover bg-center transition-opacity duration-1000"
            style={{
              backgroundImage: `url(${src})`,
              opacity: i === heroBgIndex ? 1 : 0,
              zIndex: 0,
            }}
            data-testid={`hero-bg-${i}`}
          />
        ))}
        <div className="absolute inset-0 z-0 pointer-events-none" style={{
          background: `linear-gradient(180deg, rgba(0,0,0,0.45) 0%, var(--theme-hero-overlay-color, rgba(0,0,0,0.55)) 70%, #050505 100%)`
        }} />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 w-full">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Hero Text */}
            <div className="animate-fade-in-up">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight" data-testid="hero-h1">
                {t.hero_h1_line1}<br />
                <span className="text-[#C5A059]">{t.hero_h1_line2}</span>
              </h1>
              <p className="text-lg text-white/60 mb-8 max-w-lg font-light" data-testid="hero-subtitle">
                {t.hero_subtitle}
              </p>
              <div className="flex flex-wrap gap-6 text-sm text-white/50">
                <div className="flex items-center gap-2" data-testid="hero-check-1">
                  <CheckCircle className="w-4 h-4 text-[#C5A059]" />
                  <span>{t.hero_check_1}</span>
                </div>
                <div className="flex items-center gap-2" data-testid="hero-check-2">
                  <CheckCircle className="w-4 h-4 text-[#C5A059]" />
                  <span>{t.hero_check_2}</span>
                </div>
                <div className="flex items-center gap-2" data-testid="hero-check-3">
                  <CheckCircle className="w-4 h-4 text-[#C5A059]" />
                  <span>{t.hero_check_3}</span>
                </div>
              </div>
            </div>

            {/* Booking Widget */}
            <div id="booking" className="booking-widget rounded-sm p-6 lg:p-8 animate-fade-in-up animate-delay-200" data-testid="booking-widget">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-white tracking-wide">Book Your Transfer</h2>
                {pricePreview && (
                  <div className="text-right">
                    <div className="text-2xl font-bold text-[#C5A059]" data-testid="price-display">{"\u20AC"}{pricePreview.total_price.toFixed(2)}</div>
                    <div className="text-xs text-white/50">{formData.isRoundTrip ? "Round Trip" : "One Way"}</div>
                  </div>
                )}
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* From / Swap / To — tight visual group */}
                <div className="space-y-1" data-testid="route-picker-group">
                  {/* Origin */}
                  <div>
                    <label className="form-label flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-[#C5A059]" />
                      From
                    </label>
                    <SearchableSelect
                      value={veniceDisplayLabel(formData.origin)}
                      onValueChange={handleOriginChange}
                      placeholder="Select pickup location"
                      triggerTestId="origin-select"
                      ariaLabel="Search pickup location"
                      priorityItems={origins.priority.map((loc) => ({
                        value: loc,
                        label: loc,
                        testId: `origin-priority-${loc}`,
                      }))}
                      items={origins.rest.map((loc) => ({
                        value: loc,
                        label: loc,
                        testId: `origin-option-${loc}`,
                      }))}
                    />
                    <VeniceSubPicker
                      value={formData.origin}
                      onSelect={handleVeniceOriginPick}
                      testIdPrefix="origin"
                      label="Choose pickup point"
                      nearAddress={formData.originSpecificAddress}
                      onChangeNearAddress={(v) => setFormData(prev => ({ ...prev, originSpecificAddress: v }))}
                      disabledVariant={isVeniceVariant(formData.destination) ? formData.destination : null}
                      showWaterTaxi={!!bookingSettings?.water_taxi_enabled}
                      hotelOptions={veniceHotels}
                      selectedHotelId={formData.origin === VENICE_HOTEL ? formData.veniceHotelId : null}
                      onSelectHotel={(hid) => setFormData(prev => ({ ...prev, veniceHotelId: hid }))}
                    />
                    <SpecificAddressField
                      location={formData.origin}
                      value={formData.originSpecificAddress}
                      onChange={(v) => setFormData(prev => ({ ...prev, originSpecificAddress: v }))}
                      testIdPrefix="origin"
                      sideLabel="the pickup location"
                    />
                  </div>

                  {/* Swap Button — overlaps both selects with negative margin to keep them visually attached */}
                  <div className="flex justify-center -my-3 relative z-10">
                    <button
                      type="button"
                      onClick={handleSwapRoute}
                      disabled={!formData.origin || !formData.destination}
                      className="w-9 h-9 rounded-full border border-[#C5A059]/30 bg-[#1A1A1A] flex items-center justify-center text-[#C5A059] hover:bg-[#C5A059]/20 disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-200 hover:rotate-180 shadow-md"
                      data-testid="swap-route-btn"
                      title="Swap origin and destination"
                    >
                      <ArrowDownUp className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Destination */}
                  <div>
                    <label className="form-label flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-[#C5A059]" />
                      To
                    </label>
                    <SearchableSelect
                      value={veniceDisplayLabel(formData.destination)}
                      onValueChange={handleDestinationChange}
                      placeholder={formData.origin && formData.origin !== VENICE_LABEL ? "Select destination" : "Select origin first"}
                      disabled={!formData.origin || formData.origin === VENICE_LABEL}
                      triggerTestId="destination-select"
                      ariaLabel="Search drop-off location"
                      priorityItems={destinations.priority.map((d) => ({
                        value: d.destination,
                        label: d.destination,
                        testId: `destination-priority-${d.destination}`,
                      }))}
                      items={destinations.rest.map((d) => ({
                        value: d.destination,
                        label: d.destination,
                        testId: `destination-option-${d.destination}`,
                      }))}
                    />
                  <VeniceSubPicker
                    value={formData.destination}
                    onSelect={handleVeniceDestinationPick}
                    testIdPrefix="destination"
                    label="Choose drop-off point"
                    nearAddress={formData.destinationSpecificAddress}
                    onChangeNearAddress={(v) => setFormData(prev => ({ ...prev, destinationSpecificAddress: v }))}
                    disabledVariant={isVeniceVariant(formData.origin) ? formData.origin : null}
                    showWaterTaxi={!!bookingSettings?.water_taxi_enabled}
                    hotelOptions={veniceHotels}
                    selectedHotelId={formData.destination === VENICE_HOTEL ? formData.veniceHotelId : null}
                    onSelectHotel={(hid) => setFormData(prev => ({ ...prev, veniceHotelId: hid }))}
                    /* Hotel-in-Venice as destination: available whenever the OTHER
                       side is compatible — Airport / Near Venice (airport_override
                       mode) OR a non-Venice city (per-route surcharge mode). Hidden
                       when the origin is Piazzale Roma / Water Taxi / Hotel because
                       those combos are invalid. */
                    showHotel={
                      formData.origin === VENICE_AIRPORT ||
                      formData.origin === VENICE_NEAR ||
                      (formData.origin && !isVeniceVariant(formData.origin))
                    }
                    hideAirportButton={formData.origin === VENICE_AIRPORT}
                  />
                  <SpecificAddressField
                    location={formData.destination}
                    value={formData.destinationSpecificAddress}
                    onChange={(v) => setFormData(prev => ({ ...prev, destinationSpecificAddress: v }))}
                    testIdPrefix="destination"
                    sideLabel="the drop-off location"
                  />
                  </div>
                </div>

                {/* Date and Time */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="form-label flex items-center gap-2">
                      <CalendarIcon className="w-4 h-4 text-[#C5A059]" />
                      Date
                    </label>
                    <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="input-dark w-full justify-start text-left font-normal"
                          data-testid="date-picker"
                        >
                          {formData.pickupDate ? format(formData.pickupDate, "dd MMM yyyy") : "Select date"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 bg-[#1F1F1F] border-[#333]" align="start">
                        <Calendar
                          mode="single"
                          selected={formData.pickupDate}
                          onSelect={(date) => {
                            setFormData({...formData, pickupDate: date});
                            if (date) setDatePickerOpen(false);
                          }}
                          disabled={(date) => !isDateSelectable(bookingSettings, date)}
                          className="text-white"
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div>
                    <label className="form-label flex items-center gap-2">
                      <Clock className="w-4 h-4 text-[#C5A059]" />
                      Time
                    </label>
                    <Select
                      value={formData.pickupTime}
                      onValueChange={(value) => setFormData({...formData, pickupTime: value})}
                    >
                      <SelectTrigger className="input-dark w-full" data-testid="time-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1F1F1F] border-[#333] max-h-60">
                        {filterSlotsForDate(TIME_SLOTS, bookingSettings, formData.pickupDate).map((time) => (
                          <SelectItem key={time} value={time} className="text-white hover:bg-[#333]">
                            {time}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Cutoff hint */}
                {bookingSettings && (
                  <p className="text-[#C5A059]/80 text-xs mt-1" data-testid="earliest-pickup-hint">
                    {earliestPickupLabel(bookingSettings)}
                  </p>
                )}

                {/* Night surcharge warning */}
                {isNightService() && (
                  <div className="night-badge" data-testid="night-surcharge-badge">
                    <Moon className="w-3 h-3" />
                    Night surcharge applies (20%)
                  </div>
                )}

                {/* Passengers + Luggage */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="form-label flex items-center gap-2">
                      <Users className="w-4 h-4 text-[#C5A059]" />
                      Passengers
                    </label>
                    <Select
                      value={formData.passengers.toString()}
                      onValueChange={(value) => setFormData({...formData, passengers: parseInt(value)})}
                    >
                      <SelectTrigger className="input-dark w-full" data-testid="passengers-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1F1F1F] border-[#333]">
                        {Array.from({ length: Math.max(1, fleet.max_passengers || 12) }, (_, i) => i + 1).map((num) => (
                          <SelectItem key={num} value={num.toString()} className="text-white hover:bg-[#333]">
                            {num} {num === 1 ? "passenger" : "passengers"}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="form-label flex items-center gap-2">
                      <Briefcase className="w-4 h-4 text-[#C5A059]" />
                      Luggage
                    </label>
                    <Select
                      value={(formData.luggage || 1).toString()}
                      onValueChange={(value) => setFormData({...formData, luggage: parseInt(value)})}
                    >
                      <SelectTrigger className="input-dark w-full" data-testid="luggage-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1F1F1F] border-[#333]">
                        {Array.from({ length: Math.max(1, (fleet.max_luggage || 12) + 1) }, (_, i) => i).map((num) => (
                          <SelectItem key={num} value={num.toString()} className="text-white hover:bg-[#333]">
                            {num} {num === 1 ? "bag" : "bags"}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Premium Enhancements (multi-select) — hide options whose
                    auto-assigned vehicle cannot seat the selected pax or fit
                    the selected luggage. */}
                {(() => {
                  const visibleOptions = luxuryOptions.filter((opt) => {
                    const vid = opt.auto_assign_vehicle_id;
                    if (!vid) return true;
                    const veh = (fleet.vehicles || []).find(v => v.vehicle_id === vid);
                    if (!veh) return true;  // vehicle not found — fail open
                    if ((formData.passengers || 1) > (veh.max_passengers || 0)) return false;
                    if ((formData.luggage || 0) > (veh.max_luggage || 0)) return false;
                    return true;
                  });
                  // Auto-uncheck hidden options if previously selected
                  if (formData.luxuryOptionIds?.length) {
                    const visibleIds = new Set(visibleOptions.map(o => o.luxury_option_id));
                    const stale = formData.luxuryOptionIds.filter(id => !visibleIds.has(id));
                    if (stale.length) {
                      setTimeout(() => {
                        setFormData(prev => ({
                          ...prev,
                          luxuryOptionIds: (prev.luxuryOptionIds || []).filter(id => visibleIds.has(id))
                        }));
                      }, 0);
                    }
                  }
                  if (visibleOptions.length === 0) return null;
                  return (
                  <div className="bg-[#1F1F1F]/50 rounded-sm p-4 space-y-2" data-testid="luxury-customizations">
                    <div className="flex items-center gap-2 mb-2">
                      <Star className="w-4 h-4 text-[#C5A059]" />
                      <span className="text-white text-sm font-medium">Premium enhancements <span className="text-white/40 text-xs font-normal">(optional)</span></span>
                    </div>
                    {visibleOptions.map((opt) => {
                      const checked = formData.luxuryOptionIds?.includes(opt.luxury_option_id);
                      return (
                        <label
                          key={opt.luxury_option_id}
                          className={`flex items-start gap-3 p-3 rounded-sm cursor-pointer transition-colors ${checked ? "bg-[#C5A059]/10 border border-[#C5A059]/30" : "bg-[#1F1F1F] border border-transparent hover:border-[#C5A059]/20"}`}
                          data-testid={`luxury-option-${opt.luxury_option_id}`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) => {
                              const ids = new Set(formData.luxuryOptionIds || []);
                              if (e.target.checked) ids.add(opt.luxury_option_id);
                              else ids.delete(opt.luxury_option_id);
                              setFormData({...formData, luxuryOptionIds: Array.from(ids)});
                            }}
                            className="mt-1 accent-[#C5A059] w-4 h-4"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-white text-sm font-medium">{opt.title}</span>
                              <span className="text-[10px] bg-[#C5A059]/15 text-[#C5A059] px-2 py-0.5 rounded-sm">+{opt.surcharge_pct}%</span>
                            </div>
                            {opt.short_description && (
                              <p className="text-white/50 text-xs mt-1">{opt.short_description}</p>
                            )}
                          </div>
                          {opt.full_details && (
                            <button
                              type="button"
                              onClick={(e) => { e.preventDefault(); setLuxuryDetail(opt); }}
                              className="text-[#C5A059] text-xs hover:underline shrink-0"
                              data-testid={`luxury-details-${opt.luxury_option_id}`}
                            >
                              Details
                            </button>
                          )}
                        </label>
                      );
                    })}
                  </div>
                  );
                })()}

                {/* Round Trip Option */}
                <div className={`py-3 px-4 rounded-sm transition-all ${formData.isRoundTrip ? 'bg-[#C5A059]/10 border border-[#C5A059]/30' : 'bg-[#1F1F1F]/50'}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Repeat className={`w-5 h-5 ${formData.isRoundTrip ? 'text-[#C5A059]' : 'text-white/50'}`} />
                      <div>
                        <div className="text-white text-sm font-medium flex items-center gap-2">
                          Round Trip
                          <span className="bg-emerald-500/20 text-emerald-400 text-xs px-2 py-0.5 rounded">-5%</span>
                        </div>
                        <div className="text-white/40 text-xs">Book return & save 5%</div>
                      </div>
                    </div>
                    <Switch
                      checked={formData.isRoundTrip}
                      onCheckedChange={(checked) => setFormData({...formData, isRoundTrip: checked})}
                      data-testid="roundtrip-switch"
                    />
                  </div>

                  {formData.isRoundTrip && (
                    <div className="mt-4 pt-4 border-t border-[#C5A059]/20 space-y-3">
                      <div className="text-xs text-[#C5A059] uppercase tracking-wide mb-2">Return Journey</div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-white/50 text-xs mb-1 block">Return Date *</label>
                          <Popover open={returnDatePickerOpen} onOpenChange={setReturnDatePickerOpen}>
                            <PopoverTrigger asChild>
                              <Button
                                variant="outline"
                                className="input-dark w-full justify-start text-left font-normal h-10 text-sm"
                                data-testid="return-date-picker"
                              >
                                {formData.returnDate ? format(formData.returnDate, "dd MMM") : "Select"}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0 bg-[#1F1F1F] border-[#333]" align="start">
                              <Calendar
                                mode="single"
                                selected={formData.returnDate}
                                onSelect={(date) => {
                                  setFormData({...formData, returnDate: date});
                                  if (date) setReturnDatePickerOpen(false);
                                }}
                                disabled={(date) => date < (formData.pickupDate || new Date())}
                                className="text-white"
                              />
                            </PopoverContent>
                          </Popover>
                        </div>
                        <div>
                          <label className="text-white/50 text-xs mb-1 block">Return Time *</label>
                          <Select
                            value={formData.returnTime}
                            onValueChange={(value) => setFormData({...formData, returnTime: value})}
                          >
                            <SelectTrigger className="input-dark w-full h-10 text-sm" data-testid="return-time-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-[#1F1F1F] border-[#333] max-h-60">
                              {TIME_SLOTS.map((time) => (
                                <SelectItem key={time} value={time} className="text-white hover:bg-[#333]">
                                  {time}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Price Breakdown */}
                {pricePreview && (
                  <div className="border-t border-white/10 pt-4 space-y-2" data-testid="price-breakdown">
                    <div className="flex justify-between text-sm">
                      <span className="text-white/50">Base price</span>
                      <span className="text-white">{"\u20AC"}{pricePreview.base_price.toFixed(2)}</span>
                    </div>
                    {formData.isRoundTrip && (
                      <div className="flex justify-between text-sm">
                        <span className="text-white/50">Return trip</span>
                        <span className="text-white">{"\u20AC"}{pricePreview.base_price.toFixed(2)}</span>
                      </div>
                    )}
                    {pricePreview.water_taxi_surcharge > 0 && (
                      <div className="flex justify-between text-sm" data-testid="price-water-taxi-line">
                        <span className="text-[#C5A059] flex items-center gap-1">
                          Water taxi
                          {pricePreview.water_taxi_legs > 1 && (
                            <span className="text-white/40 text-xs">
                              ({pricePreview.water_taxi_legs}× {"\u20AC"}{(pricePreview.water_taxi_surcharge / pricePreview.water_taxi_legs).toFixed(2)})
                            </span>
                          )}
                        </span>
                        <span className="text-[#C5A059]">+{"\u20AC"}{pricePreview.water_taxi_surcharge.toFixed(2)}</span>
                      </div>
                    )}
                    {pricePreview.luxury_breakdown && pricePreview.luxury_breakdown.length > 0 ? (
                      pricePreview.luxury_breakdown.map((lb) => (
                        <div key={lb.luxury_option_id} className="flex justify-between text-sm" data-testid={`price-luxury-line-${lb.luxury_option_id}`}>
                          <span className="text-white/50">{lb.title} (+{lb.surcharge_pct}%)</span>
                          <span className="text-[#C5A059]">+{"\u20AC"}{lb.amount.toFixed(2)}</span>
                        </div>
                      ))
                    ) : pricePreview.luxury_surcharge > 0 ? (
                      <div className="flex justify-between text-sm">
                        <span className="text-white/50">Luxury (+5%)</span>
                        <span className="text-[#C5A059]">+{"\u20AC"}{pricePreview.luxury_surcharge.toFixed(2)}</span>
                      </div>
                    ) : null}
                    {pricePreview.passenger_surcharge > 0 && (
                      <div className="flex justify-between text-sm" data-testid="price-pax-surcharge">
                        <span className="text-white/50 flex items-center gap-1">
                          <Users className="w-3 h-3" /> Group surcharge{pricePreview.passenger_bracket_label ? ` (${pricePreview.passenger_bracket_label})` : ""}
                        </span>
                        <span className="text-[#C5A059]">+{"\u20AC"}{pricePreview.passenger_surcharge.toFixed(2)}</span>
                      </div>
                    )}
                    {pricePreview.is_night_service && (
                      <div className="flex justify-between text-sm">
                        <span className="text-white/50 flex items-center gap-1">
                          <Moon className="w-3 h-3" /> Night surcharge
                        </span>
                        <span className="text-[#C5A059]">+{"\u20AC"}{pricePreview.night_surcharge.toFixed(2)}</span>
                      </div>
                    )}
                    {formData.isRoundTrip && pricePreview.round_trip_discount > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-emerald-400 flex items-center gap-1">
                          <Percent className="w-3 h-3" /> Round trip discount
                        </span>
                        <span className="text-emerald-400">-{"\u20AC"}{pricePreview.round_trip_discount.toFixed(2)}</span>
                      </div>
                    )}
                    {(() => {
                      const baseTotal = formData.isRoundTrip
                        ? pricePreview.base_price * 2
                        : pricePreview.base_price;
                      const extras = pricePreview.total_price - baseTotal;
                      const extrasFmt = `${extras < 0 ? "-" : "+"}\u20AC${Math.abs(extras).toFixed(2)}`;
                      return (
                        <>
                          <div className="flex justify-between pt-3 mt-1 border-t border-white/10 text-sm" data-testid="price-formula">
                            <span className="text-white/60">Base {"\u20AC"}{baseTotal.toFixed(2)} {extras !== 0 ? `+ Extras ${extrasFmt}` : ""}</span>
                            <span className="text-white/60">=</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-white font-semibold uppercase tracking-wide text-sm">Total</span>
                            <span className="text-2xl font-bold text-[#C5A059]" data-testid="price-total">{"\u20AC"}{pricePreview.total_price.toFixed(2)}</span>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                )}

                {/* Contact Info */}
                <div className="border-t border-white/10 pt-4 mt-4">
                  <h3 className="text-sm text-white/50 uppercase tracking-wide mb-4">Contact Information</h3>
                  <div className="space-y-3">
                    <Input
                      placeholder="Full Name *"
                      value={formData.userName}
                      onChange={(e) => setFormData({...formData, userName: e.target.value})}
                      className="input-dark"
                      data-testid="name-input"
                    />
                    <Input
                      type="email"
                      placeholder="Email *"
                      value={formData.userEmail}
                      onChange={(e) => setFormData({...formData, userEmail: e.target.value})}
                      className="input-dark"
                      data-testid="email-input"
                    />
                    <div>
                      <Input
                        type="tel"
                        placeholder="Phone Number * (e.g. +39 393 907 8663)"
                        value={formData.userPhone}
                        onChange={(e) => setFormData({...formData, userPhone: e.target.value})}
                        className={
                          "input-dark " +
                          (formData.userPhone && !isValidInternationalPhone(formData.userPhone)
                            ? "border-red-500/60"
                            : "")
                        }
                        data-testid="phone-input"
                        aria-invalid={formData.userPhone ? !isValidInternationalPhone(formData.userPhone) : undefined}
                      />
                      {formData.userPhone && !isValidInternationalPhone(formData.userPhone) && (
                        <p className="text-red-400 text-xs mt-1" data-testid="phone-error">
                          {INTERNATIONAL_PHONE_HINT}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                <FlightDetailsFields
                  value={formData.flightDetails}
                  onChange={(v) => setFormData({...formData, flightDetails: v})}
                />

                <div>
                  <label className="text-white/50 text-[10px] uppercase mb-1 block">Note (optional)</label>
                  <textarea
                    rows={2}
                    maxLength={1000}
                    placeholder="Anything we should know? (luggage, child seat, special requests...)"
                    value={formData.notes}
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                    className="w-full bg-[#1A1A1A] border border-[#333] rounded-sm p-2 text-white/80 text-sm resize-none focus:border-[#C5A059]/50 focus:outline-none"
                    data-testid="note-input"
                  />
                </div>

                <PrivacyCheckbox
                  checked={formData.privacyAccepted}
                  onChange={(v) => setFormData({...formData, privacyAccepted: v})}
                />

                <TermsCheckbox
                  checked={formData.termsAccepted}
                  onChange={(v) => setFormData({...formData, termsAccepted: v})}
                />

                {/* Submit */}
                <Button
                  type="submit"
                  className="btn-gold w-full mt-6 py-3 h-auto leading-tight"
                  disabled={loading}
                  data-testid="submit-quote-btn"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                      Processing...
                    </span>
                  ) : (
                    <span className="flex flex-col items-center">
                      <span className="text-sm font-semibold tracking-wide uppercase">{t.booking_cta_primary || "Get your quote"}</span>
                      <span className="text-[10px] font-normal opacity-70 normal-case mt-0.5">{t.booking_cta_subtitle || "we'll get back to you in a few minutes"}</span>
                    </span>
                  )}
                </Button>
                
                <p className="text-xs text-white/40 text-center">
                  No payment required. Our team will contact you with confirmation.
                </p>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Popular Destinations Section — replaces the legacy Popular Routes.
          Admin picks up to 3 featured category pages via `featured_order`. */}
      <PopularDestinations />

      {/* Fleet Section */}
      <section id="fleet" className="py-24 bg-[#0A0A0A]" data-testid="fleet-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 animate-fade-in-up">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4" data-testid="fleet-section-title">{t.fleet_section_title || t.prefooter_title}</h2>
            <p className="text-white/50 max-w-2xl mx-auto" data-testid="fleet-section-subtitle">
              {t.fleet_section_subtitle || t.prefooter_subtitle}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="vehicle-card p-6 animate-fade-in-up" data-testid="vehicle-vclass">
              <div className="aspect-video mb-6 overflow-hidden rounded-sm bg-[#1F1F1F]">
                <img src={resolveAsset(t.fleet_v1_image_url, HERO_BG)} alt={t.fleet_v1_name || "Mercedes V-Class"} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">{t.fleet_v1_name || "Mercedes V-Class"}</h3>
              <p className="text-white/50 text-sm mb-4">{t.fleet_v1_desc || "Premium comfort for groups up to 7 passengers"}</p>
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-4 text-white/40">
                  <span className="flex items-center gap-1"><Users className="w-4 h-4" /> {t.fleet_v1_pax || "7"}</span>
                  <span className="flex items-center gap-1"><Briefcase className="w-4 h-4" /> {t.fleet_v1_luggage || "6"}</span>
                </div>
                <span className="text-[#C5A059]">{t.fleet_v1_rate || "Standard Rate"}</span>
              </div>
            </div>

            <div className="vehicle-card p-6 animate-fade-in-up animate-delay-100" data-testid="vehicle-sclass">
              <div className="aspect-video mb-6 overflow-hidden rounded-sm bg-[#1F1F1F]">
                <img src={resolveAsset(t.fleet_v2_image_url, INTERIOR_IMG)} alt={t.fleet_v2_name || "Mercedes S-Class Interior"} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">{t.fleet_v2_name || "Mercedes S-Class"}</h3>
              <p className="text-white/50 text-sm mb-4">{t.fleet_v2_desc || "Ultimate luxury sedan for executive travel"}</p>
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-4 text-white/40">
                  <span className="flex items-center gap-1"><Users className="w-4 h-4" /> {t.fleet_v2_pax || "3"}</span>
                  <span className="flex items-center gap-1"><Briefcase className="w-4 h-4" /> {t.fleet_v2_luggage || "3"}</span>
                </div>
                <span className="text-[#C5A059]">{t.fleet_v2_rate || "+25%"}</span>
              </div>
            </div>

            <div className="vehicle-card p-6 animate-fade-in-up animate-delay-200" data-testid="vehicle-sprinter">
              <div className="aspect-video mb-6 overflow-hidden rounded-sm bg-[#1F1F1F]">
                <img src={resolveAsset(t.fleet_v3_image_url, CHAUFFEUR_IMG)} alt={t.fleet_v3_name || "Mercedes Sprinter"} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">{t.fleet_v3_name || "Mercedes Sprinter"}</h3>
              <p className="text-white/50 text-sm mb-4">{t.fleet_v3_desc || "Spacious minibus for larger groups"}</p>
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-4 text-white/40">
                  <span className="flex items-center gap-1"><Users className="w-4 h-4" /> {t.fleet_v3_pax || "12"}</span>
                  <span className="flex items-center gap-1"><Briefcase className="w-4 h-4" /> {t.fleet_v3_luggage || "12"}</span>
                </div>
                <span className="text-[#C5A059]">{t.fleet_v3_rate || "By Quote"}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <div className="grid md:grid-cols-4 gap-8 text-center">
          <div className="animate-fade-in-up" data-testid="feature-fixed">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#C5A059]/10 flex items-center justify-center">
              <Shield className="w-8 h-8 text-[#C5A059]" />
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">Fixed Rates</h3>
            <p className="text-white/50 text-sm">No surge pricing, no surprises</p>
          </div>
          <div className="animate-fade-in-up animate-delay-100" data-testid="feature-chauffeur">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#C5A059]/10 flex items-center justify-center">
              <User className="w-8 h-8 text-[#C5A059]" />
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">Pro Chauffeurs</h3>
            <p className="text-white/50 text-sm">Licensed, experienced drivers</p>
          </div>
          <div className="animate-fade-in-up animate-delay-200" data-testid="feature-cancellation">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#C5A059]/10 flex items-center justify-center">
              <CheckCircle className="w-8 h-8 text-[#C5A059]" />
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">Free Cancellation</h3>
            <p className="text-white/50 text-sm">Cancel up to 24h before</p>
          </div>
          <div className="animate-fade-in-up animate-delay-300" data-testid="feature-rating">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#C5A059]/10 flex items-center justify-center">
              <Star className="w-8 h-8 text-[#C5A059]" />
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">5-Star Rating</h3>
            <p className="text-white/50 text-sm">Trusted by thousands</p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0A0A0A] border-t border-white/5 py-12" data-testid="footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="text-2xl font-bold logo-text mb-4">{t.logo_text}</div>
              <p className="text-white/40 text-sm" data-testid="footer-about">
                {t.footer_about}
              </p>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4 uppercase text-xs tracking-wide">Routes</h4>
              <ul className="space-y-2 text-white/40 text-sm">
                <li><Link to="/routes" className="hover:text-[#C5A059] transition-colors">All Routes</Link></li>
                <li><Link to="/route/venice-to-cortina" className="hover:text-[#C5A059] transition-colors">Venice to Cortina</Link></li>
                <li><Link to="/route/venice-to-milan" className="hover:text-[#C5A059] transition-colors">Venice to Milan</Link></li>
                <li><Link to="/route/venice-to-florence" className="hover:text-[#C5A059] transition-colors">Venice to Florence</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4 uppercase text-xs tracking-wide">Services</h4>
              <ul className="space-y-2 text-white/40 text-sm">
                <li>Airport Transfers</li>
                <li>Cruise Terminal</li>
                <li>Dolomites Transfers</li>
                <li>City Transfers</li>
                <li><Link to="/faq" className="hover:text-[#C5A059] transition-colors" data-testid="footer-faq-link">FAQ</Link></li>
                <li><Link to="/why-choose-us" className="hover:text-[#C5A059] transition-colors" data-testid="footer-wcu-link">Why Choose Us</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4 uppercase text-xs tracking-wide">Contact</h4>
              <ul className="space-y-2 text-white/40 text-sm">
                <li className="flex items-center gap-2" data-testid="footer-phone">
                  <Phone className="w-4 h-4 text-[#C5A059]" />
                  {t.footer_contact_phone}
                </li>
                <li className="flex items-center gap-2" data-testid="footer-email">
                  <Mail className="w-4 h-4 text-[#C5A059]" />
                  {t.footer_contact_email}
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-white/5 mt-12 pt-8 flex flex-col md:flex-row gap-3 items-center justify-between text-white/30 text-sm">
            <div data-testid="footer-copyright">{t.footer_copyright}</div>
            <div className="flex items-center gap-4 flex-wrap justify-center" data-testid="footer-legal-links">
              <Link to="/privacy" className="hover:text-[#C5A059] transition-colors" data-testid="footer-privacy-link">Privacy Policy</Link>
              <span className="text-white/15">•</span>
              <Link to="/legal/cookies" className="hover:text-[#C5A059] transition-colors" data-testid="footer-cookies-link">Cookie Policy</Link>
              <span className="text-white/15">•</span>
              <Link to="/legal/terms" className="hover:text-[#C5A059] transition-colors">Terms</Link>
              <span className="text-white/15">•</span>
              <button
                type="button"
                onClick={() => window.dispatchEvent(new Event("tnm-open-cookie-preferences"))}
                className="hover:text-[#C5A059] transition-colors"
                data-testid="footer-cookie-preferences"
              >
                Cookie preferences
              </button>
            </div>
          </div>
        </div>
      </footer>

      {/* Luxury option details popup */}
      {luxuryDetail && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          onClick={() => setLuxuryDetail(null)}
          data-testid="luxury-detail-modal"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-[#0F0F0F] border border-white/10 rounded-sm w-full max-w-lg max-h-[85vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between p-5 border-b border-white/5">
              <div>
                <h3 className="text-lg font-semibold text-white">{luxuryDetail.title}</h3>
                <p className="text-[#C5A059] text-xs mt-1">+{luxuryDetail.surcharge_pct}% surcharge</p>
              </div>
              <button
                type="button"
                onClick={() => setLuxuryDetail(null)}
                className="text-white/40 hover:text-white text-2xl leading-none"
                data-testid="luxury-detail-close"
              >
                ×
              </button>
            </div>
            {luxuryDetail.image_url && (
              <div className="aspect-video w-full overflow-hidden bg-[#050505]" data-testid="luxury-detail-image">
                <img
                  src={luxuryDetail.image_url.startsWith("/api/")
                    ? `${process.env.REACT_APP_BACKEND_URL}${luxuryDetail.image_url}`
                    : luxuryDetail.image_url}
                  alt={luxuryDetail.title}
                  className="w-full h-full object-cover"
                  onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }}
                />
              </div>
            )}
            <article
              className="p-6 text-white/80 prose-custom prose-sm"
              dangerouslySetInnerHTML={{ __html: renderMarkdownSafe(luxuryDetail.full_details, "luxury-detail") }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
